import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  confirmPasswordReset,
  updateProfile,
} from "firebase/auth"
import { initializeApp, FirebaseApp } from "@firebase/app"
import {
  CONSOLE_ORIGIN,
  INSIGHTS_ORIGIN,
  type AuthApp,
} from "~/lib/authLinks"
import { firebaseConfig } from "~/config/firebase"

let firebaseApp: FirebaseApp | undefined

if (!firebaseApp) {
  firebaseApp = initializeApp(firebaseConfig)
}

export { firebaseApp }

const googleAuthProvider = new GoogleAuthProvider()
googleAuthProvider.addScope("https://www.googleapis.com/auth/plus.login")
googleAuthProvider.setCustomParameters({
  prompt: "select_account",
})
type LoginResult =
  | { status: "verified"; idToken: string }
  | { status: "unverified" }

type SignupResult = { status: "unverified" } | { status: "exists" }

export const loginWithGooglePopup = async (): Promise<string> => {
  try {
    const c = await signInWithPopup(getAuth(firebaseApp), googleAuthProvider)
    const idToken = await c.user.getIdToken()
    return idToken
  } catch (error) {
    console.error(
      "firebase.getAuth().signInWithPopup(googleAuthProvider)",
      error,
    )
    throw error
  }
}

export const loginWithEmailAndPassword = async (
  email: string,
  password: string,
): Promise<LoginResult> => {
  const auth = getAuth(firebaseApp)
  const userCredential = await signInWithEmailAndPassword(auth, email, password)

  if (!userCredential.user.emailVerified) {
    return { status: "unverified" }
  }

  const idToken = await userCredential.user.getIdToken()
  return { status: "verified", idToken }
}

export const signupWithEmailAndPassword = async (
  email: string,
  password: string,
  options?: { firstName?: string; lastName?: string },
): Promise<SignupResult> => {
  const auth = getAuth(firebaseApp)

  let userCredential
  try {
    userCredential = await createUserWithEmailAndPassword(auth, email, password)

    if (options?.firstName || options?.lastName) {
      const displayName = [options.firstName, options.lastName]
        .filter(Boolean)
        .join(" ")
      await updateProfile(userCredential.user, { displayName })
    }

    // Not fatal: the "Check your email" screen can resend it.
    await sendVerificationEmail().catch(() => {})
    return { status: "unverified" }
  } catch (error) {
    if ((error as { code?: string })?.code === "auth/email-already-in-use") {
      return { status: "exists" }
    }
    if (userCredential) {
      await userCredential.user.delete()
    }
    throw new Error("Unable to create account")
  }
}

const getBaseUrl = () =>
  process.env.NODE_ENV === "development"
    ? "http://localhost:5173"
    : CONSOLE_ORIGIN

// Where the "Reset your password" email returns to once the new password is
// set: the login of the product the reset was started from.
export const resetPassword = async (
  email: string,
  app: AuthApp = "console",
): Promise<void> => {
  const auth = getAuth(firebaseApp)
  await sendPasswordResetEmail(auth, email, {
    url: app === "insights" ? `${INSIGHTS_ORIGIN}/login` : `${getBaseUrl()}/login`,
  })
}

// Where to go after verifying is decided by the email action handler
// (/auth/action) from the person's access; this continue URL is only the
// fallback if the link is ever opened elsewhere.
export const sendVerificationEmail = async (): Promise<void> => {
  const auth = getAuth(firebaseApp)
  const currentUser = auth.currentUser

  if (!currentUser) {
    throw new Error("No user found")
  }

  const actionCodeSettings = {
    url: `${getBaseUrl()}/choose-product`,
  }

  try {
    await sendEmailVerification(currentUser, actionCodeSettings)
  } catch (error) {
    throw new Error("Unable to send verification email")
  }
}

export const resetPasswordWithCode = async (
  oobCode: string,
  newPassword: string,
): Promise<void> => {
  const auth = getAuth(firebaseApp)
  await confirmPasswordReset(auth, oobCode, newPassword)
}

export const logout = async (): Promise<void> => {
  const auth = getAuth(firebaseApp)
  try {
    await auth.signOut()
    await fetch("/logout", { method: "POST" })
  } catch {
    // Continue to redirect even on error
  } finally {
    window.location.href = "/"
  }
}
